import {
  Badge,
  Button,
  Flex,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Tag,
  TagCloseButton,
  TagLabel,
  Text,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";
import { ChangeEvent, useRef } from "react";
import { useTranslation } from "react-i18next";
import { FaCheck, FaPaperclip, FaUpload } from "react-icons/fa";
import { SeAttachmentSource } from "../../../autogen/bff-api";
import { useAttachmentSourceLabel } from "./attachmentSource";
import { FailedUploadChip } from "./FailedUploadChip";
import { MessageAttachmentControls } from "./useMessageAttachments";

export interface AttachmentDocumentOption {
  id: string;
  name: string;
}

interface Props {
  controls: MessageAttachmentControls;
  requestDocuments: AttachmentDocumentOption[];
  // Supplier-only source. Leave undefined for the buyer; an empty list hides the menu (e.g. no tender delivered yet).
  offerDocuments?: AttachmentDocumentOption[];
  designContest?: boolean;
}

export const AttachmentPicker = ({ controls, requestDocuments, offerDocuments, designContest = false }: Props) => {
  const {
    attachments,
    failedUploads,
    isUploading,
    formatError,
    addFromSource,
    removeAttachment,
    uploadFiles,
    retryFailedUpload,
    removeFailedUpload,
  } = controls;
  const { t } = useTranslation();
  const sourceLabel = useAttachmentSourceLabel();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const selectedIds = new Set(attachments.map((a) => a.documentId));
  const showOfferMenu = offerDocuments !== undefined && offerDocuments.length > 0;

  const onFilePicked = async (e: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    e.target.value = "";
    await uploadFiles(files);
  };

  // Documents stay listed with a checkmark when selected (clicking toggles), so the menu keeps a stable size and
  // doesn't jump while picking several in a row.
  const sourceMenu = (label: string, docs: AttachmentDocumentOption[], source: SeAttachmentSource) => (
    <Menu>
      <MenuButton as={Button} variant="outline" size="sm" leftIcon={<FaPaperclip />} isDisabled={docs.length === 0}>
        {label}
      </MenuButton>
      <MenuList maxH="300px" overflowY="auto">
        {docs.map((d) => {
          const selected = selectedIds.has(d.id);
          return (
            <MenuItem
              key={d.id}
              closeOnSelect={false}
              icon={<FaCheck style={{ opacity: selected ? 1 : 0 }} />}
              onClick={() => (selected ? removeAttachment(d.id) : addFromSource(d.id, d.name, source))}
            >
              {d.name}
            </MenuItem>
          );
        })}
      </MenuList>
    </Menu>
  );

  return (
    <Flex direction="column" gap="2">
      {formatError && (
        <Text fontSize="sm" color="smChipRedFg">
          {formatError}
        </Text>
      )}
      <Flex gap="2" wrap="wrap">
        {sourceMenu(t("From the request"), requestDocuments, SeAttachmentSource.Request)}
        {showOfferMenu && sourceMenu(designContest ? t("From the entry") : t("From the tender"), offerDocuments ?? [], SeAttachmentSource.Offer)}
        <Button
          variant="outline"
          size="sm"
          leftIcon={<FaUpload />}
          isLoading={isUploading}
          onClick={() => fileInputRef.current?.click()}
        >
          {t("Upload from disk")}
        </Button>
        <input ref={fileInputRef} type="file" multiple hidden onChange={onFilePicked} />
      </Flex>
      {(attachments.length > 0 || failedUploads.length > 0) && (
        <Wrap>
          {attachments.map((attachment) => (
            <WrapItem key={attachment.documentId}>
              <Tag>
                <TagLabel>
                  {attachment.fileName}
                  <Badge ml="2" variant="subtle">
                    {sourceLabel(attachment.source)}
                  </Badge>
                </TagLabel>
                <TagCloseButton onClick={() => removeAttachment(attachment.documentId)} />
              </Tag>
            </WrapItem>
          ))}
          {failedUploads.map((f) => (
            <WrapItem key={f.id}>
              <FailedUploadChip
                fileName={f.fileName}
                onRetry={() => retryFailedUpload(f.id)}
                onRemove={() => removeFailedUpload(f.id)}
              />
            </WrapItem>
          ))}
        </Wrap>
      )}
    </Flex>
  );
};
